import { Pressable, Text, View } from "react-native";
import { Image } from "expo-image";
import { useCSSVariable } from "uniwind";
import { Link } from "expo-router";
import { TicketPercent } from "lucide-react-native";
import type { Event } from "@lezz/shared";
import { resolveStorageUrl } from "@lezz/supabase";
import type { ParticipantPreview } from "@/features/events/lib/attach-participants-preview";
import { DistanceChip } from "@/shared/components/ui/distance-chip";
import { TagList } from "./tag-list";
import { formatEventDateOneLine } from "@/shared/lib/format-date";

type EventCardDefaultProps = {
  event: Event & {
    participantsPreview?: ParticipantPreview[];
    participantCount?: number;
  };
};

export function EventCardDefault({ event }: EventCardDefaultProps) {
  const borderColor = useCSSVariable("--color-border") as string;
  const cardEvent = useCSSVariable("--color-card-event") as string;
  const primary = useCSSVariable("--color-primary") as string;
  const participantsToShow = event.participantsPreview ?? [];
  const participantsCount = event.participantCount ?? 0;

  const allTags = [
    ...((event.eventTags as string[]) ?? []),
    ...((event.venueTags as string[]) ?? []),
    ...((event.musicTags as string[]) ?? []),
  ];

  return (
    <Link asChild href={`/event/${event.eventId}`}>
      <Pressable
        className="bg-card-event border-border-card-event mb-5 overflow-hidden rounded-lg border active:opacity-70"
        style={{ borderColor }}
        accessibilityRole="button"
        accessibilityLabel={`View details for ${event.title}`}
      >
        {event.imageUrl && (
          <Image
            source={{ uri: resolveStorageUrl(event.imageUrl) }}
            className="h-48 w-full"
            contentFit="cover"
            style={{ width: "100%", height: 192 }}
            accessibilityLabel={`Event image for ${event.title}`}
          />
        )}
        <View className="p-5">
          <Text className="font-display text-foreground text-base font-semibold">
            {event.title}
          </Text>
          {event.hostName && (
            <Text className="text-muted-foreground text-sm">
              {event.hostName}
            </Text>
          )}
          <View className="mt-2 flex-row items-center justify-between">
            <Text className="text-event-date text-sm">
              {formatEventDateOneLine(event.dateAndTime)}
            </Text>
            {event.hasCoupon && (
              <View accessibilityLabel="Este evento tem cupom">
                <TicketPercent size={20} color={primary} strokeWidth={2} />
              </View>
            )}
          </View>
          <DistanceChip lat={event.lat} lng={event.lng} className="mt-2" />

          {/* Participants preview */}
          {participantsCount > 0 && (
            <View className="mt-3 flex-row items-center">
              <View className="flex-row">
                {participantsToShow.map((participant, index) => {
                  const key =
                    participant.userId ?? participant.profileId ?? index;
                  return (
                    <View key={key}>
                      {participant.profileAvatarUrl ? (
                        <Image
                          source={{
                            uri: resolveStorageUrl(
                              participant.profileAvatarUrl
                            ),
                          }}
                          className="h-10 w-10 rounded-full border-2"
                          contentFit="cover"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 20,
                            borderColor: cardEvent,
                            borderWidth: 2,
                            marginLeft: index > 0 ? -12 : 0,
                          }}
                          accessibilityLabel={`${participant.profileDisplayName || participant.profileUsername || "Participant"}'s avatar`}
                        />
                      ) : (
                        <View
                          className="bg-brand h-10 w-10 items-center justify-center rounded-full"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 20,
                            borderColor: cardEvent,
                            borderWidth: 2,
                            marginLeft: index > 0 ? -12 : 0,
                          }}
                          accessibilityElementsHidden={true}
                        >
                          <Text className="text-brand-foreground text-xs font-bold">
                            {(
                              participant.profileDisplayName?.[0] ||
                              participant.profileUsername?.[0] ||
                              "?"
                            )?.toUpperCase()}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
              <Text className="text-muted-foreground ml-3 text-xs">
                {participantsCount === 1
                  ? "1 tem interesse"
                  : `${participantsCount} tem interesse`}
              </Text>
            </View>
          )}

          {allTags.length > 0 && (
            <View
              className="mt-4 flex-row gap-2 overflow-hidden"
              style={{ maxHeight: 40 }}
            >
              <TagList tags={allTags} />
            </View>
          )}
        </View>
      </Pressable>
    </Link>
  );
}
